import { useEffect, useState } from "react";

/**
 * Read an authoritative "now" from a server's HTTP `Date` header instead of
 * trusting the visitor's device clock. A same-origin HEAD request keeps the
 * `Date` header readable (it is not a CORS-safelisted response header), and
 * every standard HTTP server sends it in UTC.
 */
async function fetchServerDate(signal: AbortSignal): Promise<Date | null> {
  try {
    const res = await fetch(import.meta.env.BASE_URL, {
      method: "HEAD",
      cache: "no-store",
      signal,
    });
    const header = res.headers.get("date");
    if (!header) return null;
    const date = new Date(header);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

/**
 * Current date, corrected to the server clock once it resolves.
 * Starts from the local clock so the UI renders immediately, then
 * self-corrects; falls back to local time if the network is unavailable.
 */
export function useCurrentDate(): Date {
  const [now, setNow] = useState<Date>(() => new Date());

  useEffect(() => {
    const controller = new AbortController();
    fetchServerDate(controller.signal).then((date) => {
      if (date) setNow(date);
    });
    return () => controller.abort();
  }, []);

  return now;
}
